import { useEffect, useRef, useState } from 'react';
// import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import axios from '../lib/axios';
import { useAuth } from '../context/AuthContext';
import { trackEvent, identifyUser, setMerchantSuperProps } from '../services/analytics';
import { hasAnyConnectedStore } from '../utils/storeConnection';

export function ImpersonateLogin() {
    const [searchParams] = useSearchParams();
    // const navigate = useNavigate();
    // const { updateUser, selectStore } = useAuth();
    const { updateUser } = useAuth();

    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('Validating impersonation token...');

    const hasCalled = useRef(false);

    useEffect(() => {
        if (hasCalled.current) return;
        hasCalled.current = true;

        const urlToken = searchParams.get('token'); // ✅ FIX

        if (!urlToken) {
            setStatus('error');
            setMessage('No token provided');
            return;
        }

        const validateToken = async () => {
            try {
                // ✅ Clear old session first
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('stores');
                localStorage.removeItem('currentStore');
                localStorage.removeItem('impersonation_info');
                localStorage.removeItem('shop');
                localStorage.removeItem('activeStoreId');
                localStorage.removeItem('activeStoreShop');
                localStorage.removeItem('activeStore');
                localStorage.removeItem('last_used_store');

                delete axios.defaults.headers.common['Authorization'];

                // optional reset auth context
                updateUser(null as any);
                const res = await axios.post('/impersonate-login', { token: urlToken });

                if (!res.data?.success) {
                    throw new Error(res.data?.error || 'Invalid token');
                }

                // const {
                //     user,
                //     token: authToken,
                //     stores,
                //     default_store,
                //     impersonated_by
                // } = res.data;

                const {
                    user,
                    token: authToken,
                    stores,
                    default_store,
                    impersonated_by
                } = res.data;

                // ✅ Save user
                localStorage.setItem('user', JSON.stringify(user));

                // ✅ Save token
                if (authToken) {
                    localStorage.setItem('token', authToken);
                    axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
                }

                // ✅ Save stores
                // if (stores) {
                //     localStorage.setItem('stores', JSON.stringify(stores));
                // }

                // ✅ Update context
                //updateUser(user);

                // ✅ Select default store
                // if (default_store) {
                //     selectStore(default_store);
                // }
                let finalUser = user;
                let storeList: any[] = Array.isArray(stores) ? stores : [];
                let defaultShop = default_store ?? null;
                try {
                    const meRes = await axios.get('/me');
                    if (meRes.data?.user) {
                        finalUser = meRes.data.user;
                    }
                    if (Array.isArray(meRes.data?.stores)) {
                        storeList = meRes.data.stores;
                    }
                    defaultShop = meRes.data?.default_store ?? defaultShop ?? storeList[0]?.shop ?? null;
                } catch {
                    // /me unavailable - rely on the impersonate response
                }

                // ✅ Save canonical user + stores
                if (storeList.length) {
                    localStorage.setItem('stores', JSON.stringify(storeList));
                    const shopToSave = defaultShop ?? storeList[0]?.shop ?? null;
                    if (shopToSave) {
                        localStorage.setItem('shop', shopToSave);
                    } else {
                        localStorage.removeItem('shop');
                    }
                } else {
                    localStorage.removeItem('stores');
                    localStorage.removeItem('shop');
                }

                // ✅ Update context
                updateUser(finalUser);
                // updateUser merges into the stale in-memory user (the admin), so
                // re-assert the exact impersonated user after merging
                localStorage.setItem('user', JSON.stringify(finalUser));

                // ✅ Save impersonation info
                localStorage.setItem('impersonation_info', JSON.stringify({
                    impersonated_by,
                    login_time: new Date().toISOString(),
                    user_data: finalUser
                }));

                // ✅ Fire login event
                window.dispatchEvent(new CustomEvent('auth_login'));

                // ✅ Track in Mixpanel
                trackEvent('Login', {
                    user_id: user?.id,
                    email: user?.email,
                    provider: 'impersonate',
                });
                identifyUser(user?.id, {
                    $email: user?.email,
                    $name: user?.name,
                    provider: 'impersonate',
                });
                setMerchantSuperProps({ merchant_id: user?.merchant_id ?? user?.id });

                // ✅ Clean URL
                const url = new URL(window.location.href);
                url.searchParams.delete('token');
                window.history.replaceState({}, '', url.toString());

                // ✅ Redirect to scaleboard when a Shopify/WooCommerce store is
                // connected, otherwise send the merchant through onboarding
                const hasConnectedStore = hasAnyConnectedStore(storeList);
                const destination = hasConnectedStore ? '/neo-intelligence' : '/on-boarding';

                setStatus('success');
                setMessage(`Logged in as ${finalUser.name}`);
                setTimeout(() => {
                    window.location.href = destination; // better than reload
                }, 1000);
               // setTimeout(() => navigate('/'), 1000);

            } catch (err: any) {
                console.error(err);

                setStatus('error');
                setMessage(
                    err?.response?.data?.error ||
                    err.message ||
                    'Login failed'
                );
            }
        };

        validateToken();
    }, []);
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="max-w-md w-full space-y-8 p-8 text-center">

                <h2 className={`font-nunito text-[23px] font-bold leading-[1.15] tracking-[-0.035em] ${
                    status === 'success' ? 'text-[var(--okt)]' :
                    status === 'error' ? 'text-red-600' : ''
                }`}>
                    {status === 'loading' ? 'Validating Login...' :
                     status === 'success' ? 'Login Successful!' : 'Login Failed'}
                </h2>

                <p className="text-sm text-gray-500">{message}</p>

            </div>
        </div>
    );
}